import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks clicks while true. */
  loading?: boolean;
  fullWidth?: boolean;
  /** Optional leading icon element. */
  leftIcon?: ReactNode;
  /** Optional trailing icon element. */
  rightIcon?: ReactNode;
}

/** The primary tappable control. Rounded, friendly, and accessible. */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type,
  ...rest
}: ButtonProps) {
  const classes = [
    'pt-btn',
    `pt-btn--${variant}`,
    size === 'sm' ? 'pt-btn--sm' : size === 'lg' ? 'pt-btn--lg' : '',
    fullWidth ? 'pt-btn--full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="pt-btn__spinner">
          <Spinner size={18} label="Working" />
        </span>
      ) : (
        leftIcon
      )}
      {children}
      {!loading ? rightIcon : null}
    </button>
  );
}
